import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from './AuthLayout.jsx';
import { Button, Field, Input, Select, ErrorNotice, CopyField, PasswordInput } from '../../components/ui/index.js';
import { useAuth } from '../../lib/auth.jsx';
import { useMutation } from '../../hooks/useApi.js';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'JPY', 'SGD', 'AED', 'NZD'];
const COUNTRIES = [
  ['US', 'United States'], ['GB', 'United Kingdom'], ['CA', 'Canada'], ['AU', 'Australia'],
  ['IN', 'India'], ['SG', 'Singapore'], ['AE', 'United Arab Emirates'], ['NZ', 'New Zealand'],
  ['DE', 'Germany'], ['FR', 'France'], ['IE', 'Ireland'], ['NL', 'Netherlands'],
];

/**
 * MA-1: registration collects business name, email, password, country and default
 * currency. Nothing else — every additional field costs signups.
 */
export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    businessName: '', email: '', password: '', country: 'US', defaultCurrency: 'USD',
  });
  const [issuedKey, setIssuedKey] = useState(null);

  const { mutate, loading, error } = useMutation(async () => {
    const result = await register(form);
    // MA-3: the key is displayed once, on the screen that follows.
    setIssuedKey(result.secretKey);
  });

  const fieldErrors = error?.fieldErrors ?? {};
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  if (issuedKey) {
    return (
      <AuthLayout
        title="Your account is ready"
        subtitle="Here is your test API key. This is the only time it will be shown."
      >
        <div className="space-y-5">
          <CopyField
            label="Test secret key"
            value={issuedKey}
            hint="Store it in your server's environment. We keep only a hash, so it cannot be shown again — you would have to create a new one."
          />

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5">
            <p className="text-sm font-medium text-amber-700">Copy it before you continue</p>
            <p className="mt-1 text-xs text-ink-soft">
              Anyone with this key can create and refund payments on your account. Treat it like a password
              and never put it in frontend code.
            </p>
          </div>

          <Button size="lg" className="w-full" onClick={() => navigate('/dashboard', { replace: true })}>
            I&rsquo;ve saved it — continue
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Takes about a minute. You can take a test payment straight away."
      footer={<>Already have an account? <Link to="/login" className="font-medium text-accent-ink hover:underline">Sign in</Link></>}
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }} noValidate>
        {error && !Object.keys(fieldErrors).length && <ErrorNotice error={error} title="Could not create your account" />}

        <Field label="Business name" error={fieldErrors.businessName} required>
          {(props) => (
            <Input {...props} name="businessName" autoComplete="organization" autoFocus
              value={form.businessName} invalid={Boolean(fieldErrors.businessName)}
              onChange={set('businessName')} placeholder="Bean There Coffee" />
          )}
        </Field>

        <Field label="Work email" error={fieldErrors.email} required>
          {(props) => (
            <Input {...props} type="email" name="email" autoComplete="email"
              value={form.email} invalid={Boolean(fieldErrors.email)}
              onChange={set('email')} placeholder="you@company.com" />
          )}
        </Field>

        <Field
          label="Password"
          // MA-2: minimum length is the only composition rule, so say the rule
          // rather than making someone discover it by failing.
          hint="At least 10 characters. Length beats symbols."
          error={fieldErrors.password}
          required
        >
          {(props) => (
            <PasswordInput {...props}  name="password" autoComplete="new-password"
              value={form.password} invalid={Boolean(fieldErrors.password)} onChange={set('password')} />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Country" error={fieldErrors.country} required>
            {(props) => (
              <Select {...props} name="country" autoComplete="country" value={form.country} onChange={set('country')}>
                {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </Select>
            )}
          </Field>

          <Field label="Default currency" error={fieldErrors.defaultCurrency} required>
            {(props) => (
              <Select {...props} name="defaultCurrency" value={form.defaultCurrency} onChange={set('defaultCurrency')}>
                {CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
              </Select>
            )}
          </Field>
        </div>

        <Button type="submit" size="lg" loading={loading} className="w-full">Create account</Button>

        {/*
          * Said before the button, not after it, and linked rather than
          * summarised: an agreement nobody can read from the page they agree
          * on is not much of an agreement.
          */}
        <p className="text-center text-xs leading-relaxed text-muted">
          By creating an account you agree to our{' '}
          <Link to="/terms" className="underline underline-offset-2 hover:text-ink">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-ink">Privacy Policy</Link>.
        </p>
      </form>
    </AuthLayout>
  );
}

export default RegisterPage;
